/**
 * Exchange ("wymiana") explainer pop-up.
 *
 * Opens once at the start of an exchange session to explain the three steps of the
 * flow (ANSIN 2026-08, review round 4). Markup and settings: sections/exchange-popup.liquid.
 *
 * WHEN IT OPENS
 * `html.exchange-session-active` is the single source of truth for "is this customer
 * exchanging", shared with the announcement bar (assets/exchange-bar.js) and the cart
 * shipping bar (assets/global.js). This file only reads it — it never decides.
 *
 * The class can arrive two ways, and both matter here:
 *   1. Already on <html> at parse time, server-rendered by layout/theme.liquid for a
 *      cart that is already stamped. Covers every page after the first.
 *   2. Added later by exchange-session.js, when it captures a fresh session out of the
 *      `?ex=1&…` URL. That is the LANDING page — the exact moment the explainer is for
 *      — and it happens after this script's own connectedCallback, so a one-shot check
 *      would miss it. Hence the MutationObserver.
 *
 * ONCE PER SESSION
 * Keyed on the session's deadline rather than a plain "seen" flag: reloading or
 * browsing on within the same session must not reopen it, but the customer's NEXT
 * exchange, weeks later, should get the explanation again. The deadline is fixed when
 * the session is captured and never moves, so it identifies the session exactly.
 */
const EXCHANGE_POPUP_SEEN_KEY = "ansin-exchange-popup-seen";
const EXCHANGE_ROOT_CLASS = "exchange-session-active";

class ExchangePopup extends ModalDialog {
  constructor() {
    super();
    this.delay = Number(this.dataset.delay || 0) * 1000;
    this.observer = null;
  }

  connectedCallback() {
    // Deliberately NOT calling super.connectedCallback(): that moves the element to
    // the end of <body>, and this one already renders there inside the overlay
    // section group. The newsletter pop-up skips it for the same reason.
    if (Shopify.designMode) {
      if (this.dataset.openInDesignMode === "true") this.show();
      return;
    }

    if (this.tryShow()) return;
    this.watchForSession();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
    clearTimeout(this.timer);
  }

  /** @returns {string} The active session's deadline, or "" when not in a session. */
  sessionKey() {
    const root = document.documentElement;
    if (!root.classList.contains(EXCHANGE_ROOT_CLASS)) return "";
    return root.dataset.exchangeDeadline || "";
  }

  seen(key) {
    try {
      return localStorage.getItem(EXCHANGE_POPUP_SEEN_KEY) === key;
    } catch {
      // Private mode / storage disabled. Falling through means the pop-up may show
      // again on the next page, which is the kinder of the two failure modes —
      // the other is never explaining the flow at all.
      return false;
    }
  }

  markSeen(key) {
    try {
      localStorage.setItem(EXCHANGE_POPUP_SEEN_KEY, key);
    } catch {
      /* private mode */
    }
  }

  /** @returns {boolean} Whether a session was found and the pop-up scheduled. */
  tryShow() {
    const key = this.sessionKey();
    if (!key || this.seen(key)) return false;

    this.timer = setTimeout(() => {
      // Re-read rather than trusting the key from `delay` seconds ago: a session
      // that lapsed while the timer ran must not produce an explainer for a flow
      // the customer is no longer in.
      const current = this.sessionKey();
      if (!current || this.seen(current)) return;
      this.markSeen(current);
      this.show();
    }, this.delay);

    return true;
  }

  /**
   * Waits for exchange-session.js to capture a session out of the URL. Both the
   * class and the deadline attribute are set in the same synchronous `sync()` call,
   * so either mutation is a safe trigger.
   */
  watchForSession() {
    this.observer = new MutationObserver(() => {
      if (!this.tryShow()) return;
      this.observer.disconnect();
      this.observer = null;
    });

    this.observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-exchange-deadline"],
    });
  }
}

customElements.define("exchange-popup", ExchangePopup);
